import { useEffect, useMemo, useState } from "react";

import {
  getClosestTimeSlotFromDate,
  formatDisplayDateTime,
  TIME_SLOTS,
} from "./calendar.helpers";

interface UseCalendarDateTimeOptions {
  value?: string | null; // ISO
  onChange: (value: string | null) => void;
}

const formatDateOnly = (date: Date | null) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const useCalendarDateTime = ({
  value,
  onChange,
}: UseCalendarDateTimeOptions) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");

  // sincroniza o estado interno toda vez que o dialog abre
  useEffect(() => {
    if (!open) return;

    // 1) Tentativa com o value vindo de fora
    if (value) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        setSelectedDate(d);

        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const time = `${hh}:${mm}`;

        if (TIME_SLOTS.includes(time)) {
          // Horário exato está na lista
          setSelectedTime(time);
        } else {
          // Não está na lista -> escolhe o mais próximo
          setSelectedTime(getClosestTimeSlotFromDate(d));
        }

        return;
      }
    }

    // 2) Sem value ou value inválido -> padrão: hoje + slot mais próximo de agora
    const now = new Date();
    setSelectedDate(now);
    setSelectedTime(getClosestTimeSlotFromDate(now));
  }, [open, value]);

  const daysMatrix = useMemo(() => {
    const ref = selectedDate ?? new Date();
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const firstDay = new Date(year, month, 1);
    const startWeekDay = firstDay.getDay(); // 0 = dom
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const matrix: (number | null)[][] = [];
    let week: (number | null)[] = [];

    for (let i = 0; i < startWeekDay; i++) week.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      week.push(day);
      if (week.length === 7) {
        matrix.push(week);
        week = [];
      }
    }

    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      matrix.push(week);
    }

    return { matrix, year, month };
  }, [selectedDate]);

  const handleMonthChange = (delta: -1 | 1) => {
    const base = selectedDate ?? new Date();
    const d = new Date(base);
    d.setMonth(d.getMonth() + delta);
    setSelectedDate(d);
  };

  const handleDayClick = (day: number) => {
    const base = selectedDate ?? new Date();
    const d = new Date(base);
    d.setDate(day);
    setSelectedDate(d);
  };

  const handleTimeClick = (time: string) => setSelectedTime(time);

  const handleClear = () => {
    setSelectedDate(null);
    setSelectedTime("");
    onChange(null);
  };

  const handleConfirm = () => {
    // Regra de segurança: sem data ou hora -> não faz nada
    if (!selectedDate || !selectedTime) {
      return;
    }

    const [hh, mm] = selectedTime.split(":").map(Number);
    const d = new Date(selectedDate);
    d.setHours(hh, mm, 0, 0);
    onChange(d.toISOString());
    setOpen(false);
  };

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  const selectedDateLabel = formatDateOnly(selectedDate);
  const displayText = formatDisplayDateTime(value);

  return {
    // estado
    state: {
      open,
      selectedDate,
      selectedTime,
      daysMatrix,
      selectedDateLabel,
      displayText,
    },

    // ações
    actions: {
      openDialog,
      closeDialog,
      handleMonthChange,
      handleDayClick,
      handleTimeClick,
      handleClear,
      handleConfirm,
    },
  };
};
